// Iframe <-> parent-page postMessage bridge for embedded contexts
// (e.g., Reshet's WordPress admin embedding the LTS CMS).
//
// Target origin is resolved at runtime against an allowlist so messages
// only go to a known parent and never to a wildcard "*".

const ALLOWED_PARENTS = (process.env.REACT_APP_PARENT_ORIGINS || "")
  .split(",")
  .map((s) => s.trim())
  .filter(Boolean);

function currentParentOrigin() {
  if (window.parent === window) return null;
  // Chromium + Safari: ancestorOrigins is the clean source.
  const anc = window.location.ancestorOrigins;
  if (anc && anc.length) return ALLOWED_PARENTS.includes(anc[0]) ? anc[0] : null;
  // Firefox fallback: ancestorOrigins is not implemented; fall back to referrer.
  try {
    const ref = new URL(document.referrer).origin;
    return ALLOWED_PARENTS.includes(ref) ? ref : null;
  } catch {
    return null;
  }
}

export function notifyParent(type, itemId) {
  const target = currentParentOrigin();
  if (!target) return; // not in iframe, or parent not in allowlist
  window.parent.postMessage({ type, itemId }, target);
}

// Ask the embedding parent for a JWT. Resolves to { token, userdata } or null.
//
// Accepts two message shapes from the parent:
//   1. AUTH_RESPONSE — the reply to our REQUEST_AUTH (our handshake spec)
//   2. AUTH_TOKEN    — an unsolicited push (Reshet's preferred shape, where the
//                      WP page posts the token as soon as it has one without
//                      waiting to be asked)
// Both may include an optional `userdata` payload — if present, the caller can
// hydrate the user state directly and skip a /api/me round-trip.
//
// We still send REQUEST_AUTH on mount for two reasons:
//   - Parents following our spec only reply when asked
//   - Parents using push may have fired before our listener was registered;
//     REQUEST_AUTH prompts them to resend
export function requestAuthFromParent({ timeoutMs = 3000 } = {}) {
  const parentOrigin = currentParentOrigin();
  if (!parentOrigin) return Promise.resolve(null);

  return new Promise((resolve) => {
    const onMessage = (e) => {
      if (e.origin !== parentOrigin) return;
      const t = e.data?.type;
      if (t !== "AUTH_RESPONSE" && t !== "AUTH_TOKEN") return;
      const token = e.data.token;
      if (!token) return;
      cleanup();
      resolve({ token, userdata: e.data.userdata || null });
    };
    const timeout = setTimeout(() => {
      cleanup();
      resolve(null);
    }, timeoutMs);
    const cleanup = () => {
      window.removeEventListener("message", onMessage);
      clearTimeout(timeout);
    };
    window.addEventListener("message", onMessage);
    // IFRAME_READY is an explicit "our listener is live, safe to send a token"
    // ping. Reshet's parent pushes AUTH_TOKEN on its own iframe onload, which can
    // fire before our bundle mounts and this listener exists — that early push is
    // dropped. We emit IFRAME_READY (and REQUEST_AUTH) only AFTER registering the
    // listener, so a parent that waits for either lands the token every time.
    window.parent.postMessage({ type: "IFRAME_READY" }, parentOrigin);
    window.parent.postMessage({ type: "REQUEST_AUTH" }, parentOrigin);
  });
}

// Confirm to the embedding parent that login completed and the CMS is now
// authenticated — the ack that closes the handshake loop, so an embedder knows
// the token it sent was accepted (vs. silently dropped). Fired on both the
// auto-login (AUTH_RESPONSE/AUTH_TOKEN) and manual login paths. Includes the
// user identity so the parent can correlate who's signed in. No-op when not
// embedded or the parent isn't allowlisted.
export function notifyParentLoginSuccess() {
  const target = currentParentOrigin();
  if (!target) return;
  let ud = null;
  try {
    ud = JSON.parse(window.localStorage.getItem("userdata"));
  } catch {
    // no/invalid userdata — still send the ack without identity fields
  }
  window.parent.postMessage(
    {
      type: "LOGIN_SUCCESS",
      userId: ud?.id ?? null,
      email: ud?.email ?? null,
      role: ud?.role ?? null,
    },
    target
  );
}

// The failure counterpart of LOGIN_SUCCESS: tell the parent auto-login (or a
// manual attempt) did NOT complete, so its loader drops instead of hanging.
// `reason` distinguishes the cases: "no_token" (parent sent nothing usable in
// time), "invalid_token" (token was rejected), "invalid_credentials" (manual
// login rejected). No-op when not embedded.
export function notifyParentLoginFailed(reason) {
  const target = currentParentOrigin();
  if (!target) return;
  window.parent.postMessage({ type: "LOGIN_FAILED", reason: reason ?? null }, target);
}

// ── Inbound navigation (parent → CMS) ──────────────────────────────────────
// Lets WordPress point the embedded CMS at a specific item WITHOUT juggling the
// iframe URL, by posting:
//
//   { type: "NAVIGATE", target: "editor",     itemId: <content id>   }  → /editor/<id>
//   { type: "NAVIGATE", target: "highlight",  itemId: <highlight id> }  → /highlights?id=<id>
//   { type: "NAVIGATE", target: "highlights" }                          → /highlights (list)
//   { type: "NAVIGATE", target: "content"    }                          → /content (list)
//
// Safety: only messages from an ALLOWLISTED parent are honored, only these known
// internal routes are reachable, and itemId is validated to a strict id charset —
// so a malicious frame can't drive an open redirect or path traversal. `id`/`view`
// are accepted as aliases for `itemId`/`target` so this is forgiving of the exact
// shape the WP side settles on. Returns the resolved internal path, or null.
const SAFE_ID = /^[A-Za-z0-9-]{1,64}$/;

export function resolveNavPath(data) {
  if (!data || data.type !== "NAVIGATE") return null;
  const id =
    typeof data.itemId === "string" ? data.itemId
      : typeof data.id === "string" ? data.id
        : null;
  const validId = id && SAFE_ID.test(id) ? id : null;
  switch (data.target ?? data.view) {
    case "editor":
      return validId ? `/editor/${validId}` : null;
    case "highlight":
      return validId ? `/highlights?id=${validId}` : null;
    case "highlights":
      return "/highlights";
    case "content":
      return "/content";
    default:
      return null;
  }
}

let navBridgeInstalled = false;

// Install the inbound-navigation listener. Additive and side-effect-free unless a
// well-formed NAVIGATE arrives from the allowlisted parent — so it can't disturb
// the existing auth handshake or item-event flow. Navigation is performed with the
// app's own convention (pushState + popstate), so the router re-routes AND the
// url-reactive pages re-read via the history observer's `locationchange`.
export function installParentNavigationBridge() {
  if (navBridgeInstalled || typeof window === "undefined") return;
  navBridgeInstalled = true;
  window.addEventListener("message", (e) => {
    const parent = currentParentOrigin();
    if (!parent || e.origin !== parent) return; // only the allowlisted embedder
    const path = resolveNavPath(e.data);
    if (!path) return;
    if (path === window.location.pathname + window.location.search) return; // already there
    window.history.pushState({}, "", path);
    window.dispatchEvent(new PopStateEvent("popstate"));
  });
}
